import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box } from '@rover/kibble/core';
import { searchFilterKeysMap } from '@rover/shared/js/search/utilities/searchFiltersMap';
import { getURL } from '@rover/shared/js/utils/getFullUrl';
import { redirect } from '@rover/utilities/url';

import getBackToSearchUrl from '../../../components/getBackToSearchUrl';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';

import PetCompatibilityOutcome from './PetCompatibilityOutcome';
import PetCompatibilityQuestion from './PetCompatibilityQuestion';
import usePetCompatibilityAnswers from './usePetCompatibilityAnswers';

type Props = {
  onDismiss: () => void;
};

const PetCompatibilityBanner = ({ onDismiss }: Props): JSX.Element | null => {
  const { sitterData } = useRevampProfileContext();
  const location = useLocation();
  const { enabled, petName } = sitterData.petCompatibility;
  const {
    questions,
    answered,
    currentQuestion,
    filters,
    hasSavedToPets,
    pendingAnswer,
    hasSaveError,
    answer,
  } = usePetCompatibilityAnswers(sitterData.petCompatibility.questions);
  const [isDismissed, setIsDismissed] = useState(false);

  if (!enabled || questions.length === 0 || isDismissed) return null;

  const searchNow = (): void => {
    const url = new URL(
      getBackToSearchUrl(new URLSearchParams(location.search)).toString(),
      getURL()
    );
    url.searchParams.delete('page');
    Object.entries(filters).forEach(([filter, isOn]) => {
      const param = searchFilterKeysMap[filter as keyof typeof searchFilterKeysMap];
      if (isOn && param) url.searchParams.set(param, 'true');
    });
    redirect(url);
  };

  return (
    <Box data-testid="pet-compatibility-banner">
      {currentQuestion ? (
        <PetCompatibilityQuestion
          key={currentQuestion}
          question={currentQuestion}
          sitterName={sitterData.firstName}
          petName={petName}
          position={answered.length + 1}
          total={questions.length}
          pendingAnswer={pendingAnswer}
          hasSaveError={hasSaveError}
          onAnswer={answer}
        />
      ) : (
        <PetCompatibilityOutcome
          answered={answered}
          petName={petName}
          hasSavedToPets={hasSavedToPets}
          onSearchNow={searchNow}
          onClose={() => {
            setIsDismissed(true);
            onDismiss();
          }}
        />
      )}
    </Box>
  );
};

export default PetCompatibilityBanner;
